import * as React from "react";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableSortLabel from "@mui/material/TableSortLabel";
import Box from "@mui/material/Box";
import {visuallyHidden} from "@mui/utils";
import {ReactComponentElement, useEffect} from "react";
import {TextField, useTheme} from "@mui/material";
import InputAdornment from "@mui/material/InputAdornment";
import {Search} from "@mui/icons-material";
import {LoadingOutlined} from "@ant-design/icons";

interface TableSearchProps {
    colSpan: number;
    handleSearch: {
        (searchValue: string): void
    }
}

function TableSearch(props: TableSearchProps) {
    const SEARCH_DELAY = 500;

    const [searchVal, setSearchVal] = React.useState('');
    const [shrink, setShrink] = React.useState(false);

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchVal(e.target.value)
    }

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            props.handleSearch(searchVal)
        }, SEARCH_DELAY)

        return () => clearTimeout(delayDebounceFn)
    }, [searchVal])

    return (
        <TableCell colSpan={props.colSpan} sx={{padding: '16px'}}>
            <TextField
                size="small"
                sx={{
                    '& .MuiInputLabel-root:not(.MuiInputLabel-shrink)': {
                        transform: "translate(41px, 10px)"
                    }
                }}
                onFocus={() => setShrink(true)}
                onBlur={(e) => {
                    !e.target.value && setShrink(false);
                }}
                label='Search'
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <Search/>
                        </InputAdornment>
                    )
                }}
                InputLabelProps={{
                    shrink: shrink,
                }}
                onChange={handleSearchChange}
            />
        </TableCell>
    )
}

export interface HeadCellConfig {
    key: any;
    label: string | ReactComponentElement<any>;
    formatter?: {
        (rawValue: any): any;
    };
    sortable?: boolean;
}

interface EnhancedTableHeadProps {
    headers: HeadCellConfig[];
    onSortChange: (event: React.MouseEvent<unknown>, property: any) => void;
    order: 'asc' | 'desc';
    orderBy: string;
    handleSearch: any;
    canEdit: boolean;
    backgroundLoading?: boolean;
}

export default (props: EnhancedTableHeadProps) => {
    const {order, orderBy, onSortChange} = props;
    const theme = useTheme()

    const createSortHandler = (property: any) => (event: React.MouseEvent<unknown>) => {
        onSortChange(event, property);
    };

    const colSpan = props.canEdit ? props.headers.length + 1 : props.headers.length;
    const searchColSpan = props.backgroundLoading ? colSpan - 2 : colSpan;
    return (
        <TableHead>
            <TableRow>
                <TableSearch colSpan={searchColSpan} handleSearch={props.handleSearch}/>
                {props.backgroundLoading && (
                    <TableCell colSpan={2} sx={{textAlign: 'right'}}>
                        <LoadingOutlined title={"Refreshing"}
                                style={{color: theme.palette.primary.main, fontSize: '25px'}}/>
                    </TableCell>
                )
                }

            </TableRow>
            <TableRow>
                {
                    props.headers.map((headCell, i) => {
                        if (headCell.sortable === false) {
                            return (
                                <TableCell
                                    colSpan={i === props.headers.length - 1 ? 2 : 1}
                                    key={headCell.key}
                                    align='left'
                                >
                                    {headCell.label}
                                </TableCell>
                            )
                        }
                        return (
                            <TableCell
                                colSpan={i === props.headers.length - 1 ? 2 : 1}
                                key={headCell.key}
                                align='left'
                                sortDirection={orderBy === headCell.key ? order : false}
                            >
                                <TableSortLabel
                                    active={orderBy === headCell.key}
                                    direction={orderBy === headCell.key ? order : 'asc'}
                                    onClick={createSortHandler(headCell.key)}
                                >
                                    {headCell.label}
                                    {orderBy === headCell.key ? (
                                        <Box component="span" sx={visuallyHidden}>
                                            {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                                        </Box>
                                    ) : null}
                                </TableSortLabel>
                            </TableCell>
                        )
                    })
                }
            </TableRow>
        </TableHead>
    );
}
